import React, { useEffect, useState } from "react";
import { adminApiService } from "../../../service/admin/adminApiService";
import Loader from "../../../component/common/loader";
import "./style.css";

// Supported domains - will be fetched from backend
const DEFAULT_DOMAINS = ['importerr.com', 'importerr.ae', 'boundless.co.in'];
const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

export default function PricingAlgorithms() {
  const [algorithms, setAlgorithms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [domains, setDomains] = useState(DEFAULT_DOMAINS);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAlgo, setEditingAlgo] = useState(null);
  const [message, setMessage] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorResult, setCalculatorResult] = useState(null);
  const [showAlgorithmModal, setShowAlgorithmModal] = useState(false);
  const [showSavedAlgoModal, setShowSavedAlgoModal] = useState(false);
  const [savedAlgoResult, setSavedAlgoResult] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAlgo, setViewingAlgo] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'soft' or 'hard'
  const [deleteAlgoId, setDeleteAlgoId] = useState(null);

  // HSN related states
  const [hsnMappings, setHsnMappings] = useState([]);
  const [selectedHsnCategory, setSelectedHsnCategory] = useState('');
  const [selectedHsnMapping, setSelectedHsnMapping] = useState(null);
  const [loadingHsn, setLoadingHsn] = useState(false);

  const [form, setForm] = useState({
    name: "",
    domain: "importerr.com",
    scope: "common",
    categoryId: "",
    hsnId: "",
    priority: 1,
    active: true,
    skus: [],
    formula: {
      rmb_to_inr_rate: 14.5,
      markup_percent: 0.15,
      customs_duty_default: 0.22,
      customs_duty_surcharge: 0.10,
      international_freight_per_kg: 600,
      weight_buffer_percent: 0.05,
      default_chargeable_weight: 0.25,
      last_mile_base: 60,
      last_mile_extra_per_500g: 30,
      platform_fee_percent: 0.00,
      gst_default: 0.18,
      gst_freight: 0.18,
      gst_platform_fee: 0.18,
      local_shipping_rmb: 0
    }
  });

  const [skuText, setSkuText] = useState("");
  const [skuSearchTerm, setSkuSearchTerm] = useState("");
  const [skuSearchResults, setSkuSearchResults] = useState([]);
  const [isSkuSearching, setIsSkuSearching] = useState(false);

  const [calculator, setCalculator] = useState({
    ap: 10,
    ls: 3,
    hsn: false,
    hsnTaxPercent: 12,
    dw: 0.3,
    vw: 0.25,
    categoryId: "",
    formula: {
      rmb_to_inr_rate: 14.5,
      markup_percent: 0.15,
      customs_duty_default: 0.22,
      customs_duty_surcharge: 0.10,
      international_freight_per_kg: 600,
      weight_buffer_percent: 0.05,
      default_chargeable_weight: 0.25,
      last_mile_base: 60,
      last_mile_extra_per_500g: 30,
      platform_fee_percent: 0.00,
      gst_default: 0.18,
      gst_freight: 0.18,
      gst_platform_fee: 0.18,
      local_shipping_rmb: 0
    }
  });

  const normalizeSkuList = (input) => {
    if (!input) return [];
    const items = Array.isArray(input) ? input : String(input).split(/[\s,;\n\r]+/g);
    const seen = new Set();
    const normalized = [];
    items.forEach((item) => {
      if (item === null || item === undefined) return;
      const value = String(item).trim().toUpperCase();
      if (!value) return;
      if (!seen.has(value)) {
        seen.add(value);
        normalized.push(value);
      }
    });
    return normalized;
  };

  const handleSkuTextChange = (value) => {
    setSkuText(value);
    const parsed = normalizeSkuList(value);
    setForm((prev) => ({ ...prev, skus: parsed }));
  };

  const addSku = (sku) => {
    const parsed = normalizeSkuList([sku]);
    if (!parsed.length) return;
    const normalizedSku = parsed[0];
    setForm((prev) => {
      // Check if SKU already exists (case-insensitive)
      const exists = prev.skus.some(existingSku =>
        String(existingSku).trim().toUpperCase() === normalizedSku
      );
      if (exists) return prev;
      const updated = [...prev.skus, normalizedSku];
      setSkuText(updated.join(", "));
      return { ...prev, skus: updated };
    });
    setSkuSearchTerm("");
    setSkuSearchResults([]);
  };

  const removeSku = (sku) => {
    setForm((prev) => {
      // Normalize both for comparison (case-insensitive)
      const normalizedSku = String(sku).trim().toUpperCase();
      const updated = prev.skus.filter((item) => String(item).trim().toUpperCase() !== normalizedSku);
      setSkuText(updated.join(", "));
      return { ...prev, skus: updated };
    });
  };

  useEffect(() => {
    loadAlgorithms();
    loadCategories();
    loadDomains();
  }, []);

  useEffect(() => {
    // Only sync skuText when scope changes, not when skus change manually
    // This prevents conflicts with manual SKU removal/addition
    if (form.scope !== "sku") {
      setSkuText("");
    }
  }, [form.scope]);

  useEffect(() => {
    const fetchHsnMappings = async () => {
      const categoryToFetch = form.categoryId;
      if (!categoryToFetch) {
        setHsnMappings([]);
        setSelectedHsnMapping(null);
        // also clear any selected hsnId in the form
        setForm(prev => ({ ...prev, hsnId: "" }));
        return;
      }
      setLoadingHsn(true);
      try {
        const token = localStorage.getItem('LOGIN_HEADER');
        const response = await fetch(`${BACKEND_API}admin/hsn-mappings/category/${encodeURIComponent(categoryToFetch)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const result = await response.json();
          const mappings = result.data || [];
          setHsnMappings(mappings);
          // Prefer existing form.hsnId if present and found in mappings
          const existingHsnId = form.hsnId;
          let toSelect = null;
          if (existingHsnId) {
            toSelect = mappings.find(m => m._id === existingHsnId) || mappings.find(m => String(m.hsnCode) === String(existingHsnId));
          }
          if (!toSelect && mappings.length > 0) {
            toSelect = mappings[0];
          }
          if (toSelect) {
            setSelectedHsnMapping(toSelect);
            setForm(prev => ({ ...prev, hsnId: toSelect._id || String(toSelect.hsnCode || '') }));
          } else {
            setSelectedHsnMapping(null);
            setForm(prev => ({ ...prev, hsnId: "" }));
          }
        } else {
          // Fallback: filter from all mappings
          const allResponse = await fetch(`${BACKEND_API}admin/hsn-mappings?category=${encodeURIComponent(categoryToFetch)}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (allResponse.ok) {
            const result = await allResponse.json();
            const mappings = result.data || [];
            setHsnMappings(mappings);
            // Prefer existing form.hsnId if present
            const existingHsnId = form.hsnId;
            let toSelect = null;
            if (existingHsnId) {
              toSelect = mappings.find(m => m._id === existingHsnId) || mappings.find(m => String(m.hsnCode) === String(existingHsnId));
            }
            if (!toSelect && mappings.length > 0) toSelect = mappings[0];
            if (toSelect) {
              setSelectedHsnMapping(toSelect);
              setForm(prev => ({ ...prev, hsnId: toSelect._id || String(toSelect.hsnCode || '') }));
            } else {
              setSelectedHsnMapping(null);
              setForm(prev => ({ ...prev, hsnId: "" }));
            }
          }
        }
      } catch (err) {
        console.error('[HSN] Error fetching mappings:', err);
      } finally {
        setLoadingHsn(false);
      }
    };

    fetchHsnMappings();
  }, [form.categoryId]);

  useEffect(() => {
    if (form.scope !== "sku") {
      setSkuSearchResults([]);
      setIsSkuSearching(false);
      return;
    }

    const term = skuSearchTerm.trim();
    if (term.length < 2) {
      setSkuSearchResults([]);
      setIsSkuSearching(false);
      return;
    }

    setIsSkuSearching(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await adminApiService.get("/pricing-algorithms/sku-search", { q: term, limit: 10 });
        if (!cancelled) {
          const results = res?.data || [];
          setSkuSearchResults(results.filter((item) => !form.skus.includes(item.sku)));
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to search SKUs", err);
          setSkuSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSkuSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [skuSearchTerm, form.scope, form.skus]);

  const scopeLabels = {
    common: "Common",
    category: "Category Specific",
    sku: "SKU Based Custom Scope",
  };

  const loadAlgorithms = async () => {
    try {
      setInitialLoading(true);
      const res = await adminApiService.get("/pricing-algorithms", { _t: Date.now() });
      console.log("Full API response:", res);
      console.log("Response data:", res?.data);
      console.log("Algorithms array:", res?.data);
      console.log("Array length:", res?.data?.length);
      setAlgorithms(res?.data || []);
    } catch (err) {
      console.error("Failed to load algorithms:", err);
      setMessage("Failed to load algorithms");
    } finally {
      setInitialLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await adminApiService.get("/category");
      setCategories(res?.data || []);
    } catch (err) {
      console.error("Failed to load categories", err);
    }
  };

  const loadDomains = async () => {
    try {
      const res = await adminApiService.get("/pricing-algorithms/domains");
      if (res?.data && Array.isArray(res.data)) {
        setDomains(res.data);
      }
    } catch (err) {
      console.error("Failed to load domains, using defaults", err);
      // Keep default domains
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      setLoading(true);
      const payload = { ...form };
      if (payload.scope === "sku") {
        // Ensure we're using the current form.skus array
        const currentSkus = Array.isArray(form.skus) ? form.skus : [];
        payload.skus = normalizeSkuList(currentSkus);
        delete payload.categoryId;
        if (!payload.skus.length) {
          setMessage("Please add at least one SKU for the SKU scope.");
          setLoading(false);
          return;
        }
        console.log('Submitting SKU-based algorithm:', {
          id: editingAlgo?._id,
          skus: payload.skus,
          originalFormSkus: form.skus
        });
      } else {
        delete payload.skus;
        if (!payload.categoryId || payload.categoryId === "") {
          delete payload.categoryId;
        }
      }
      let response;
      if (editingAlgo) {
        console.log('Updating algorithm payload:', payload);
        response = await adminApiService.put(`/pricing-algorithms/${editingAlgo._id}`, payload);
      } else {
        response = await adminApiService.post("/pricing-algorithms", payload);
      }
      const baseMessage =
        response?.status?.message ||
        (editingAlgo ? "Algorithm updated successfully" : "Algorithm created successfully");
      const missingSkus = Array.isArray(response?.data?.missingSkus) ? response.data.missingSkus : [];
      const finalMessage =
        missingSkus.length > 0
          ? `${baseMessage}. Missing SKUs (not found in catalog): ${missingSkus.join(", ")}`
          : baseMessage;
      setMessage(finalMessage);
      setShowForm(false);
      setEditingAlgo(null);
      resetForm();
      await loadAlgorithms();
    } catch (err) {
      const errorMessage = err?.response?.data?.error?.message || "Failed to save algorithm";
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (algo) => {
    setEditingAlgo(algo);
    const skus = Array.isArray(algo.skus) ? algo.skus : [];
    setForm({
      name: algo.name,
      domain: algo.domain || "importerr.com",
      scope: algo.scope,
      categoryId: algo.categoryId?._1d || algo.categoryId?._id || "",
      hsnId: algo.hsnId?._id || algo.hsnId || "",
      priority: algo.priority,
      active: algo.active,
      skus: skus,
      formula: { ...algo.formula }
    });
    // Set SKU text immediately when editing
    if (algo.scope === "sku" && skus.length > 0) {
      setSkuText(skus.join(", "));
    } else {
      setSkuText("");
    }
    setSkuSearchTerm("");
    setSkuSearchResults([]);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    setDeleteAlgoId(id);
    setDeleteType('soft');
    setShowDeleteModal(true);
  };

  const handleHardDelete = (id) => {
    setDeleteAlgoId(id);
    setDeleteType('hard');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      if (deleteType === 'hard') {
        await adminApiService.delete(`/pricing-algorithms/${deleteAlgoId}/hard`);
        setMessage("Algorithm permanently deleted");
      } else {
        await adminApiService.delete(`/pricing-algorithms/${deleteAlgoId}`);
        setMessage("Algorithm deleted successfully");
      }
      setShowDeleteModal(false);
      loadAlgorithms();
    } catch (err) {
      setMessage(`Failed to ${deleteType === 'hard' ? 'permanently ' : ''}delete algorithm`);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (algo) => {
    setViewingAlgo(algo);
    setShowViewModal(true);
  };

  const handleSavedAlgorithmTest = async () => {
    try {
      console.log("Starting saved algorithm test with data:", calculator);
      setLoading(true);
      const res = await adminApiService.post("/pricing-algorithms/calculate", calculator);
      console.log("Saved algorithm response:", res);
      setSavedAlgoResult(res?.data);
      setShowSavedAlgoModal(true);
    } catch (err) {
      console.error("Error with saved algorithm:", err);
      setMessage("Failed to calculate with saved algorithm: " + (err.response?.data?.error?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromCalculator = () => {
    const timestamp = new Date().toLocaleString();
    const newForm = {
      name: `Calculator Algorithm ${timestamp}`,
      scope: calculator.categoryId ? "category" : "common",
      categoryId: calculator.categoryId || "",
      priority: 1,
      active: true,
      skus: [],
      formula: { ...calculator.formula }
    };
    console.log("Setting form with:", newForm);
    setForm(newForm);
    setSkuText("");
    setSkuSearchTerm("");
    setSkuSearchResults([]);
    setShowAlgorithmModal(true);
  };

  const handleSaveAlgorithmFromModal = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      setLoading(true);
      const payload = { ...form };
      if (payload.scope === "sku") {
        payload.skus = normalizeSkuList(form.skus);
        delete payload.categoryId;
        if (!payload.skus.length) {
          setMessage("Please add at least one SKU for the SKU scope.");
          setLoading(false);
          return;
        }
      } else {
        delete payload.skus;
        if (!payload.categoryId || payload.categoryId === "") {
          delete payload.categoryId;
        }
      }
      console.log("Sending form data:", payload);
      const response = await adminApiService.post("/pricing-algorithms", payload);
      const baseMessage =
        response?.status?.message || "Algorithm created successfully from calculator values";
      const missingSkus = Array.isArray(response?.data?.missingSkus) ? response.data.missingSkus : [];
      const finalMessage =
        missingSkus.length > 0
          ? `${baseMessage}. Missing SKUs (not found in catalog): ${missingSkus.join(", ")}`
          : baseMessage;
      setMessage(finalMessage);
      setShowAlgorithmModal(false);
      resetForm();
      await loadAlgorithms();
    } catch (err) {
      console.error("Error saving algorithm:", err);
      const errorMessage = err?.response?.data?.error?.message || err.message || "Failed to save algorithm";
      setMessage("Failed to save algorithm: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const calculateLocally = () => {
    const { ap, ls, hsn, hsnTaxPercent, dw, formula } = calculator;

    // AP in INR = AP (RMB) × RMB to INR rate
    const AP_INR = Number(ap || 0) * Number(formula.rmb_to_inr_rate || 14.5);

    // Markup Price = AP × markup %
    const MP = AP_INR * Number(formula.markup_percent || 0.15);

    // Cost Price = AP + MP
    const CP = AP_INR + MP;

    // Local Shipping (use provided ls or formula's local_shipping_rmb, convert to INR)
    const lsRmb = Number(ls || 0) || Number(formula.local_shipping_rmb || 0);
    const LS = lsRmb * Number(formula.rmb_to_inr_rate || 14.5);

    // Customs Duty calculation
    let CD;
    if (hsn && hsnTaxPercent) {
      const dutyPercent = Number(hsnTaxPercent) / 100;
      const surcharge = dutyPercent * Number(formula.customs_duty_surcharge || 0.10);
      CD = (CP + LS) * (dutyPercent + surcharge);
    } else {
      CD = (CP + LS) * Number(formula.customs_duty_default || 0.22);
    }

    // International Freight
    let chargeableWeight;
    if (dw && dw > 0) {
      chargeableWeight = Number(dw) + (Number(dw) * Number(formula.weight_buffer_percent || 0.05));
    } else {
      chargeableWeight = Number(formula.default_chargeable_weight || 0.25);
    }
    const IF = chargeableWeight * Number(formula.international_freight_per_kg || 600);

    // Last Mile Delivery
    const baseWeight = 0.5;
    let LMD = Number(formula.last_mile_base || 60);
    if (chargeableWeight > baseWeight) {
      const extraWeight = chargeableWeight - baseWeight;
      const extra500gUnits = Math.ceil(extraWeight / 0.5);
      LMD += extra500gUnits * Number(formula.last_mile_extra_per_500g || 30);
    }

    // Landing Cost
    const LC = CP + LS + CD + IF + LMD;

    // Platform Fee
    const PF = LC * Number(formula.platform_fee_percent || 0);

    // GST calculations
    let GST_PR;
    if (hsn && hsnTaxPercent) {
      GST_PR = (CP + LS + CD) * (Number(hsnTaxPercent) / 100);
    } else {
      GST_PR = (CP + LS + CD) * Number(formula.gst_default || 0.18);
    }

    const GST_FR = (IF + LMD) * Number(formula.gst_freight || 0.18);
    const GST_PF = PF * Number(formula.gst_platform_fee || 0.18);

    // Final Buyer Price
    const FBP = LC + PF + GST_PR + GST_FR + GST_PF;

    return {
      algorithm: "Local Calculation",
      breakdown: {
        AP_RMB: Number(ap || 0),
        AP_INR: Math.round(AP_INR),
        MP: Math.round(MP),
        CP: Math.round(CP),
        LS: Math.round(LS),
        CD: Math.round(CD),
        IF: Math.round(IF),
        LMD: Math.round(LMD),
        LC: Math.round(LC),
        PF: Math.round(PF),
        GST_PR: Math.round(GST_PR),
        GST_FR: Math.round(GST_FR),
        GST_PF: Math.round(GST_PF),
        FBP: Math.round(FBP),
        chargeableWeight: Math.round(chargeableWeight * 1000) / 1000
      }
    };
  };

  const handleLocalCalculate = () => {
    const result = calculateLocally();
    setCalculatorResult(result);
  };

  const resetForm = () => {
    setForm({
      name: "",
      domain: "importerr.com",
      scope: "common",
      categoryId: "",
      priority: 1,
      active: true,
      skus: [],
      formula: {
        rmb_to_inr_rate: 14.5,
        markup_percent: 0.15,
        customs_duty_default: 0.22,
        customs_duty_surcharge: 0.10,
        international_freight_per_kg: 600,
        weight_buffer_percent: 0.05,
        default_chargeable_weight: 0.25,
        last_mile_base: 60,
        last_mile_extra_per_500g: 30,
        platform_fee_percent: 0.00,
        gst_default: 0.18,
        gst_freight: 0.18,
        gst_platform_fee: 0.18,
        local_shipping_rmb: 0
      }
    });
    setSkuText("");
    setSkuSearchTerm("");
    setSkuSearchResults([]);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("formula.")) {
      const formulaKey = name.split(".")[1];
      setForm(prev => ({
        ...prev,
        formula: {
          ...prev.formula,
          [formulaKey]: type === "number" ? Number(value) : value
        }
      }));
    } else if (name === "scope") {
      const nextScope = value;
      setForm(prev => ({
        ...prev,
        scope: nextScope,
        categoryId: nextScope === "category" ? prev.categoryId : "",
        skus: nextScope === "sku" ? prev.skus : []
      }));
      if (nextScope !== "sku") {
        setSkuText("");
        setSkuSearchTerm("");
        setSkuSearchResults([]);
      }
    } else {
      setForm(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : (type === "number" ? Number(value) : value)
      }));
    }
  };

  const handleCalculatorChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("formula.")) {
      const formulaKey = name.split(".")[1];
      setCalculator(prev => ({
        ...prev,
        formula: {
          ...prev.formula,
          [formulaKey]: type === "number" ? Number(value) : value
        }
      }));
    } else {
      setCalculator(prev => ({
        ...prev,
        [name]: type === "checkbox" ? checked : (type === "number" ? Number(value) : value)
      }));
    }
  };

  return (
    <div className="pa-container">
      <div className="pa-header">
        <div className="pa-header-actions">
          <button
            type="button"
            onClick={() => setShowCalculator(!showCalculator)}
            className="pa-btn pa-btn-green"
          >
            {showCalculator ? "Hide Calculator" : "Price Calculator"}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowForm(true);
              setEditingAlgo(null);
              resetForm();
            }}
            className="pa-btn pa-btn-blue"
          >
            Add Algorithm
          </button>
        </div>
      </div>

      {message && (
        <div className="pa-message">
          {message}
        </div>
      )}

      {/* Price Calculator */}
      {showCalculator && (
        <div className="pa-section">
          <h3 className="text-lg font-semibold mb-4">Price Calculator & Algorithm Tester</h3>

          {/* Product Input Fields */}
          <div className="mb-6">
            <h4 className="text-md font-semibold mb-3">Product Details</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">AP (Alibaba Price in RMB)</label>
                <input
                  type="number"
                  name="ap"
                  value={calculator.ap}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="e.g., 10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">LS (Local Shipping in RMB)</label>
                <input
                  type="number"
                  name="ls"
                  value={calculator.ls}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="e.g., 3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">DW (Dead Weight in kg)</label>
                <input
                  type="number"
                  name="dw"
                  value={calculator.dw}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.001"
                  placeholder="e.g., 0.3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  name="categoryId"
                  value={calculator.categoryId}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.c_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="hsn"
                    checked={calculator.hsn}
                    onChange={handleCalculatorChange}
                    className="mr-2"
                  />
                  Has HSN Code
                </label>
              </div>
              {calculator.hsn && (
                <div>
                  <label className="block text-sm font-medium mb-1">HSN Tax %</label>
                  <input
                    type="number"
                    name="hsnTaxPercent"
                    value={calculator.hsnTaxPercent}
                    onChange={handleCalculatorChange}
                    className="w-full p-2 border rounded"
                    step="0.1"
                    placeholder="e.g., 12"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Formula Configuration */}
          <div className="mb-6">
            <h4 className="text-md font-semibold mb-3">Algorithm Formula (Test & Modify)</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">RMB to INR Rate</label>
                <input
                  type="number"
                  name="formula.rmb_to_inr_rate"
                  value={calculator.formula.rmb_to_inr_rate}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Markup %</label>
                <input
                  type="number"
                  name="formula.markup_percent"
                  value={calculator.formula.markup_percent}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customs Duty Default</label>
                <input
                  type="number"
                  name="formula.customs_duty_default"
                  value={calculator.formula.customs_duty_default}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customs Surcharge</label>
                <input
                  type="number"
                  name="formula.customs_duty_surcharge"
                  value={calculator.formula.customs_duty_surcharge}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Freight per KG</label>
                <input
                  type="number"
                  name="formula.international_freight_per_kg"
                  value={calculator.formula.international_freight_per_kg}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Weight Buffer %</label>
                <input
                  type="number"
                  name="formula.weight_buffer_percent"
                  value={calculator.formula.weight_buffer_percent}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default Weight (kg)</label>
                <input
                  type="number"
                  name="formula.default_chargeable_weight"
                  value={calculator.formula.default_chargeable_weight}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Mile Base</label>
                <input
                  type="number"
                  name="formula.last_mile_base"
                  value={calculator.formula.last_mile_base}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Mile Extra/500g</label>
                <input
                  type="number"
                  name="formula.last_mile_extra_per_500g"
                  value={calculator.formula.last_mile_extra_per_500g}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Platform Fee %</label>
                <input
                  type="number"
                  name="formula.platform_fee_percent"
                  value={calculator.formula.platform_fee_percent}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GST Default %</label>
                <input
                  type="number"
                  name="formula.gst_default"
                  value={calculator.formula.gst_default}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GST Freight %</label>
                <input
                  type="number"
                  name="formula.gst_freight"
                  value={calculator.formula.gst_freight}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">LS (Local Shipping RMB)</label>
                <input
                  type="number"
                  name="formula.local_shipping_rmb"
                  value={calculator.formula.local_shipping_rmb}
                  onChange={handleCalculatorChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0"
                />
                <small className="text-gray-500">LS(INR) = LS(RMB) × Rate</small>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mb-4">
            <button
              onClick={handleLocalCalculate}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Calculate Price (Local)
            </button>
            <button
              onClick={handleSavedAlgorithmTest}
              disabled={loading}
              className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
            >
              Calculate with Saved Algorithm
            </button>
            <button
              onClick={handleCreateFromCalculator}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create Algorithm from These Values
            </button>
          </div>

          {calculatorResult && (
            <div className="mt-4 p-4 bg-white border rounded">
              <h4 className="font-semibold mb-4">Complete Price Breakdown (Algorithm: {calculatorResult.algorithm})</h4>

              {/* Step by step calculation */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-blue-50 rounded">
                  <div><strong>Step 1: Convert to INR</strong></div>
                  <div>AP (RMB): {calculatorResult.breakdown.AP_RMB}</div>
                  <div>Rate: {calculator.formula.rmb_to_inr_rate}</div>
                  <div>AP (INR): ₹{calculatorResult.breakdown.AP_INR}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-green-50 rounded">
                  <div><strong>Step 2: Add Markup</strong></div>
                  <div>Markup: ₹{calculatorResult.breakdown.MP}</div>
                  <div>Cost Price: ₹{calculatorResult.breakdown.CP}</div>
                  <div>Local Ship: ₹{calculatorResult.breakdown.LS}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-yellow-50 rounded">
                  <div><strong>Step 3: Duties & Freight</strong></div>
                  <div>Customs Duty: ₹{calculatorResult.breakdown.CD}</div>
                  <div>Int'l Freight: ₹{calculatorResult.breakdown.IF}</div>
                  <div>Last Mile: ₹{calculatorResult.breakdown.LMD}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-purple-50 rounded">
                  <div><strong>Step 4: Landing Cost</strong></div>
                  <div>Landing Cost: ₹{calculatorResult.breakdown.LC}</div>
                  <div>Platform Fee: ₹{calculatorResult.breakdown.PF}</div>
                  <div>Chargeable Wt: {calculatorResult.breakdown.chargeableWeight} kg</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-red-50 rounded">
                  <div><strong>Step 5: GST</strong></div>
                  <div>GST Product: ₹{calculatorResult.breakdown.GST_PR}</div>
                  <div>GST Freight: ₹{calculatorResult.breakdown.GST_FR}</div>
                  <div>GST Platform: ₹{calculatorResult.breakdown.GST_PF}</div>
                </div>

                <div className="p-4 bg-green-100 border-2 border-green-300 rounded text-center">
                  <div className="text-2xl font-bold text-green-800">
                    Final Buyer Price (FBP): ₹{calculatorResult.breakdown.FBP}
                  </div>
                  <div className="text-sm text-green-600 mt-1">
                    LC + PF + GST_PR + GST_FR + GST_PF = ₹{calculatorResult.breakdown.LC} + ₹{calculatorResult.breakdown.PF} + ₹{calculatorResult.breakdown.GST_PR} + ₹{calculatorResult.breakdown.GST_FR} + ₹{calculatorResult.breakdown.GST_PF}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Algorithm Form */}
      {showForm && (
        <div className="pa-section">
          <h3 className="text-lg font-semibold mb-4">
            {editingAlgo ? "Edit Algorithm" : "Create Algorithm"}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Domain *</label>
                <select
                  name="domain"
                  value={form.domain}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  required
                >
                  {domains.map(domain => (
                    <option key={domain} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Scope *</label>
                <select
                  name="scope"
                  value={form.scope}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="common">Common</option>
                  <option value="category">Category Specific</option>
                  <option value="sku">SKU Based Custom Scope</option>
                </select>
              </div>
              {form.scope === "category" && (
                <div>
                  <label className="block text-sm font-medium mb-1">Category*</label>
                  <select
                    name="categoryId"
                    value={form.categoryId}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.c_name}</option>
                    ))}
                  </select>
                </div>
              )}
              {form.categoryId && (
                <div>
                  <label className="block text-sm font-medium mb-1">HSN Mapping</label>
                  <select
                    name="hsnId"
                    value={form.hsnId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setForm(prev => ({ ...prev, hsnId: id }));
                      const mapping = hsnMappings.find(m => m._id === id) || null;
                      setSelectedHsnMapping(mapping);
                    }}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select HSN Mapping</option>
                    {loadingHsn ? (
                      <option value="">Loading...</option>
                    ) : hsnMappings.length > 0 ? (
                      hsnMappings.map(m => (
                        <option key={m._id} value={m._id}>
                          {m.Item || m.category || m.hsnCode || m.HSN || m.name || m._id}
                        </option>
                      ))
                    ) : (
                      <option value="">No HSN mappings</option>
                    )}
                  </select>
                </div>
              )}
              {form.scope === "sku" && (
                <div className="md:col-span-4 col-span-1">
                  <label className="block text-sm font-medium mb-1">Assign SKUs *</label>
                  <textarea
                    value={skuText}
                    onChange={(e) => handleSkuTextChange(e.target.value)}
                    className="w-full p-2 border rounded"
                    rows={3}
                    placeholder="Enter comma or newline separated SKUs"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste comma/newline separated SKUs or use search below. Duplicates are ignored automatically.
                  </p>
                  {form.skus.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {form.skus.map((sku) => (
                        <span
                          key={sku}
                          className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded"
                        >
                          {sku}
                          <button
                            type="button"
                            onClick={() => removeSku(sku)}
                            className="ml-1 text-blue-600 hover:text-blue-900"
                            title="Remove SKU"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-1">Search & Add SKUs</label>
                    <input
                      type="text"
                      value={skuSearchTerm}
                      onChange={(e) => setSkuSearchTerm(e.target.value)}
                      className="w-full p-2 border rounded"
                      placeholder="Start typing a SKU..."
                    />
                    <div className="mt-2 space-y-2">
                      {isSkuSearching && (
                        <div className="text-xs text-gray-500">Searching...</div>
                      )}
                      {!isSkuSearching && skuSearchTerm.trim().length < 2 && (
                        <div className="text-xs text-gray-500">
                          Type at least 2 characters to search for SKUs.
                        </div>
                      )}
                      {!isSkuSearching && skuSearchTerm.trim().length >= 2 && skuSearchResults.length === 0 && (
                        <div className="text-xs text-gray-500">No matching SKUs found.</div>
                      )}
                      {skuSearchResults.map((result) => (
                        <div
                          key={result.sku}
                          className="flex items-center justify-between border rounded px-3 py-2 text-sm bg-white"
                        >
                          <div className="pr-4">
                            <div className="font-semibold text-gray-700">{result.sku}</div>
                            {result.title && (
                              <div className="text-xs text-gray-500 truncate max-w-xs">{result.title}</div>
                            )}
                            {result.categoryName && (
                              <div className="text-xs text-gray-400">Category: {result.categoryName}</div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => addSku(result.sku)}
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Priority</label>
                <input
                  type="number"
                  name="priority"
                  value={form.priority}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  min="1"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="active"
                  checked={form.active}
                  onChange={handleChange}
                  className="mr-2"
                />
                Active
              </label>
            </div>

            <h4 className="text-md font-semibold mb-3">Formula Configuration</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">RMB to INR Rate</label>
                <input
                  type="number"
                  name="formula.rmb_to_inr_rate"
                  value={form.formula.rmb_to_inr_rate}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.1"
                  placeholder="14.5"
                />
                <small className="text-gray-500">AP(INR) = AP(RMB) × Rate</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Markup Percent</label>
                <input
                  type="number"
                  name="formula.markup_percent"
                  value={form.formula.markup_percent}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.15"
                />
                <small className="text-gray-500">MP = AP × Markup%</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customs Duty Default</label>
                <input
                  type="number"
                  name="formula.customs_duty_default"
                  value={form.formula.customs_duty_default}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.22"
                />
                <small className="text-gray-500">When HSN not available</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Customs Duty Surcharge</label>
                <input
                  type="number"
                  name="formula.customs_duty_surcharge"
                  value={form.formula.customs_duty_surcharge}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.10"
                />
                <small className="text-gray-500">Additional 10% on duty</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Int'l Freight per KG (INR)</label>
                <input
                  type="number"
                  name="formula.international_freight_per_kg"
                  value={form.formula.international_freight_per_kg}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="600"
                />
                <small className="text-gray-500">IF = CW × Rate</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Weight Buffer %</label>
                <input
                  type="number"
                  name="formula.weight_buffer_percent"
                  value={form.formula.weight_buffer_percent}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.05"
                />
                <small className="text-gray-500">CW = DW + (DW × 5%)</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Default Weight (kg)</label>
                <input
                  type="number"
                  name="formula.default_chargeable_weight"
                  value={form.formula.default_chargeable_weight}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.25"
                />
                <small className="text-gray-500">When weight unknown</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Mile Base (INR)</label>
                <input
                  type="number"
                  name="formula.last_mile_base"
                  value={form.formula.last_mile_base}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="60"
                />
                <small className="text-gray-500">For ≤500g</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Last Mile Extra per 500g</label>
                <input
                  type="number"
                  name="formula.last_mile_extra_per_500g"
                  value={form.formula.last_mile_extra_per_500g}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  placeholder="30"
                />
                <small className="text-gray-500">For each extra 500g</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Platform Fee %</label>
                <input
                  type="number"
                  name="formula.platform_fee_percent"
                  value={form.formula.platform_fee_percent}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.00"
                />
                <small className="text-gray-500">Currently 0%</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GST Default %</label>
                <input
                  type="number"
                  name="formula.gst_default"
                  value={form.formula.gst_default}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.18"
                />
                <small className="text-gray-500">When HSN not available</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">GST on Freight %</label>
                <input
                  type="number"
                  name="formula.gst_freight"
                  value={form.formula.gst_freight}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0.18"
                />
                <small className="text-gray-500">On IF + LMD</small>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">LS (Local Shipping RMB)</label>
                <input
                  type="number"
                  name="formula.local_shipping_rmb"
                  value={form.formula.local_shipping_rmb}
                  onChange={handleChange}
                  className="w-full p-2 border rounded"
                  step="0.01"
                  placeholder="0"
                />
                <small className="text-gray-500">LS(INR) = LS(RMB) × Rate</small>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {loading ? "Saving..." : (editingAlgo ? "Update" : "Create")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingAlgo(null);
                  resetForm();
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Algorithms List */}
      <div className="pa-card" style={{ position: "relative", minHeight: 120 }}>
        <Loader value={initialLoading} variant="overlay" text="Loading algorithms..." />
        <div className="pa-card-head">
          <h2>Algorithms List</h2>
        </div>
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Scope</th>
                <th>Category</th>
                <th>Assigned SKUs</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {algorithms.map((algo) => (
                <tr key={algo._id} className={algo.deleted ? 'pa-row-deleted' : ''}>
                  <td className="pa-cell-name">
                    {algo.name}
                    {algo.deleted && <span className="pa-tag pa-tag-status-deleted">DELETED</span>}
                  </td>
                  <td>
                    <span className="pa-tag pa-tag-domain">
                      {algo.domain || 'importerr.com'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`pa-tag ${algo.scope === 'common'
                        ? 'pa-tag-scope-common'
                        : algo.scope === 'category'
                          ? 'pa-tag-scope-category'
                          : algo.scope === 'sku'
                            ? 'pa-tag-scope-sku'
                            : 'pa-tag-domain'
                        }`}
                    >
                      {scopeLabels[algo.scope] || algo.scope}
                    </span>
                  </td>
                  <td>{algo.categoryId?.name || '-'}</td>
                  <td>
                    {algo.scope === 'sku'
                      ? (algo.skus && algo.skus.length
                        ? `${algo.skus.slice(0, 3).join(", ")}${algo.skus.length > 3 ? ` +${algo.skus.length - 3}` : ""}`
                        : 'None')
                      : '-'}
                  </td>
                  <td>{algo.priority}</td>
                  <td>
                    <span
                      className={`pa-tag ${algo.deleted ? 'pa-tag-status-deleted' : algo.active ? 'pa-tag-status-active' : 'pa-tag-status-inactive'
                        }`}
                    >
                      {algo.deleted ? 'Deleted' : algo.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(algo.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="pa-table-actions">
                      <button type="button" onClick={() => handleView(algo)} title="View">👁️</button>
                      <button type="button" onClick={() => handleEdit(algo)} title="Edit">✏️</button>
                      <button type="button" onClick={() => handleDelete(algo._id)} title="Soft Delete">🗑️</button>
                      <button type="button" onClick={() => handleHardDelete(algo._id)} title="Hard Delete">❌</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {algorithms.length === 0 && (
            <div className="pa-empty">
              No algorithms found. Create your first algorithm to get started.
            </div>
          )}
        </div>
      </div>

      {/* Create Algorithm Modal */}
      {showAlgorithmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-96 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create Algorithm from Calculator</h3>
            <form onSubmit={handleSaveAlgorithmFromModal}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Algorithm Name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Domain</label>
                  <select
                    name="domain"
                    value={form.domain}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                    required
                  >
                    {domains.map(domain => (
                      <option key={domain} value={domain}>{domain}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scope</label>
                  <select
                    name="scope"
                    value={form.scope}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="common">Common (All Categories)</option>
                    <option value="category">Category Specific</option>
                    <option value="sku">SKU Based Custom Scope</option>
                  </select>
                </div>
                {form.scope === "category" && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Category</label>
                    <select
                      name="categoryId"
                      value={form.categoryId}
                      onChange={handleChange}
                      className="w-full p-2 border rounded"
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.c_name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {form.scope === "sku" && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Assign SKUs *</label>
                    <textarea
                      value={skuText}
                      onChange={(e) => handleSkuTextChange(e.target.value)}
                      className="w-full p-2 border rounded"
                      rows={3}
                      placeholder="Enter comma or newline separated SKUs"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Paste comma/newline separated SKUs or use search below. Duplicates are ignored automatically.
                    </p>
                    {form.skus.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {form.skus.map((sku) => (
                          <span
                            key={sku}
                            className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded"
                          >
                            {sku}
                            <button
                              type="button"
                              onClick={() => removeSku(sku)}
                              className="ml-1 text-blue-600 hover:text-blue-900"
                              title="Remove SKU"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-4">
                      <label className="block text-sm font-medium mb-1">Search & Add SKUs</label>
                      <input
                        type="text"
                        value={skuSearchTerm}
                        onChange={(e) => setSkuSearchTerm(e.target.value)}
                        className="w-full p-2 border rounded"
                        placeholder="Start typing a SKU..."
                      />
                      <div className="mt-2 space-y-2">
                        {isSkuSearching && (
                          <div className="text-xs text-gray-500">Searching...</div>
                        )}
                        {!isSkuSearching && skuSearchTerm.trim().length < 2 && (
                          <div className="text-xs text-gray-500">
                            Type at least 2 characters to search for SKUs.
                          </div>
                        )}
                        {!isSkuSearching && skuSearchTerm.trim().length >= 2 && skuSearchResults.length === 0 && (
                          <div className="text-xs text-gray-500">No matching SKUs found.</div>
                        )}
                        {skuSearchResults.map((result) => (
                          <div
                            key={result.sku}
                            className="flex items-center justify-between border rounded px-3 py-2 text-sm bg-white"
                          >
                            <div className="pr-4">
                              <div className="font-semibold text-gray-700">{result.sku}</div>
                              {result.title && (
                                <div className="text-xs text-gray-500 truncate max-w-xs">{result.title}</div>
                              )}
                              {result.categoryName && (
                                <div className="text-xs text-gray-400">Category: {result.categoryName}</div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => addSku(result.sku)}
                              className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Add
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <input
                    type="number"
                    name="priority"
                    value={form.priority}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                    min="1"
                  />
                </div>
                <div className="text-sm text-gray-600">
                  <p>Formula values from calculator will be saved with this algorithm.</p>
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {loading ? "Saving..." : "Save Algorithm"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAlgorithmModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Saved Algorithm Result Modal */}
      {showSavedAlgoModal && savedAlgoResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Saved Algorithm Result</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600">Algorithm Used: <strong>{savedAlgoResult.algorithm}</strong></p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div className="p-2 bg-blue-50 rounded">AP (RMB): {savedAlgoResult.breakdown.AP_RMB}</div>
              <div className="p-2 bg-blue-50 rounded">AP (INR): ₹{savedAlgoResult.breakdown.AP_INR}</div>
              <div className="p-2 bg-green-50 rounded">MP: ₹{savedAlgoResult.breakdown.MP}</div>
              <div className="p-2 bg-green-50 rounded">CP: ₹{savedAlgoResult.breakdown.CP}</div>
              <div className="p-2 bg-yellow-50 rounded">LS: ₹{savedAlgoResult.breakdown.LS}</div>
              <div className="p-2 bg-yellow-50 rounded">CD: ₹{savedAlgoResult.breakdown.CD}</div>
              <div className="p-2 bg-purple-50 rounded">IF: ₹{savedAlgoResult.breakdown.IF}</div>
              <div className="p-2 bg-purple-50 rounded">LMD: ₹{savedAlgoResult.breakdown.LMD}</div>
              <div className="p-2 bg-red-50 rounded">LC: ₹{savedAlgoResult.breakdown.LC}</div>
              <div className="p-2 bg-red-50 rounded">PF: ₹{savedAlgoResult.breakdown.PF}</div>
              <div className="p-2 bg-gray-50 rounded">GST_PR: ₹{savedAlgoResult.breakdown.GST_PR}</div>
              <div className="p-2 bg-gray-50 rounded">GST_FR: ₹{savedAlgoResult.breakdown.GST_FR}</div>
              <div className="p-2 bg-gray-50 rounded">GST_PF: ₹{savedAlgoResult.breakdown.GST_PF}</div>
              <div className="p-2 bg-gray-50 rounded">CW: {savedAlgoResult.breakdown.chargeableWeight} kg</div>
            </div>
            <div className="p-4 bg-green-100 border-2 border-green-300 rounded text-center mb-4">
              <div className="text-xl font-bold text-green-800">
                Final Buyer Price: ₹{savedAlgoResult.breakdown.FBP}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowSavedAlgoModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Algorithm Modal */}
      {showViewModal && viewingAlgo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Algorithm Details</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><strong>Name:</strong> {viewingAlgo.name}</div>
              <div><strong>Domain:</strong> <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800">{viewingAlgo.domain || 'importerr.com'}</span></div>
              <div><strong>Scope:</strong> {scopeLabels[viewingAlgo.scope] || viewingAlgo.scope}</div>
              <div><strong>Category:</strong> {viewingAlgo.categoryId?.name || 'All Categories'}</div>
              <div><strong>Priority:</strong> {viewingAlgo.priority}</div>
              <div><strong>Status:</strong> {viewingAlgo.active ? 'Active' : 'Inactive'}</div>
              <div><strong>Created:</strong> {new Date(viewingAlgo.createdAt).toLocaleDateString()}</div>
              {viewingAlgo.scope === "sku" && (
                <div className="col-span-2">
                  <strong>Assigned SKUs:</strong>
                  {viewingAlgo.skus && viewingAlgo.skus.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {viewingAlgo.skus.map((sku) => (
                        <span key={sku} className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                          {sku}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 mt-1">None</div>
                  )}
                </div>
              )}
            </div>
            <h4 className="font-semibold mb-2">Formula Configuration</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-4">
              <div className="p-2 bg-gray-50 rounded">RMB Rate: {viewingAlgo.formula.rmb_to_inr_rate}</div>
              <div className="p-2 bg-gray-50 rounded">Markup %: {(viewingAlgo.formula.markup_percent * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">Customs Duty: {(viewingAlgo.formula.customs_duty_default * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">Customs Surcharge: {(viewingAlgo.formula.customs_duty_surcharge * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">Freight/KG: ₹{viewingAlgo.formula.international_freight_per_kg}</div>
              <div className="p-2 bg-gray-50 rounded">Weight Buffer: {(viewingAlgo.formula.weight_buffer_percent * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">Default Weight: {viewingAlgo.formula.default_chargeable_weight}kg</div>
              <div className="p-2 bg-gray-50 rounded">Last Mile Base: ₹{viewingAlgo.formula.last_mile_base}</div>
              <div className="p-2 bg-gray-50 rounded">Last Mile Extra: ₹{viewingAlgo.formula.last_mile_extra_per_500g}</div>
              <div className="p-2 bg-gray-50 rounded">Platform Fee: {(viewingAlgo.formula.platform_fee_percent * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">GST Default: {(viewingAlgo.formula.gst_default * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">GST Freight: {(viewingAlgo.formula.gst_freight * 100).toFixed(1)}%</div>
              <div className="p-2 bg-gray-50 rounded">LS (RMB): {viewingAlgo.formula.local_shipping_rmb || 0}</div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-red-600">
              {deleteType === 'hard' ? 'Permanent Delete' : 'Delete Algorithm'}
            </h3>
            <p className="text-gray-700 mb-6">
              {deleteType === 'hard'
                ? 'Are you sure you want to PERMANENTLY delete this algorithm? This action cannot be undone and the algorithm will be completely removed from the database.'
                : 'Are you sure you want to delete this algorithm? It will be marked as deleted but can be restored later.'
              }
            </p>
            <div className="flex space-x-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className={`px-4 py-2 text-white rounded hover:opacity-90 ${deleteType === 'hard' ? 'bg-red-600' : 'bg-orange-600'
                  }`}
              >
                {loading ? 'Deleting...' : deleteType === 'hard' ? 'Permanently Delete' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}